import React, { useState, useEffect, useRef } from 'react';
import {
  Typography,
  Box,
  Alert
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import BaseModal from './common/BaseModal';
import ConfirmationDialog from './ConfirmationDialog';
import { getSentence, getLanguage, getUppyLocal } from './translations';
import { transcribe } from './Network';
import { matomoEvent } from './matomoTracker';
import { ServerURL } from './ServerURL';

/* eslint-disable */
import Uppy from '@uppy/core';
import XHR from '@uppy/xhr-upload';
import { Dashboard, ProgressBar } from '@uppy/react';

// Import Uppy styles
import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';
import '@uppy/drag-drop/dist/style.css';
import '@uppy/file-input/dist/style.css';
import '@uppy/progress-bar/dist/style.css';

const UploadModal = ({
  open,
  onClose,
  onUploadSuccess,
  isRecording = false
}) => {
  const [uppy, setUppy] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const uppyRef = useRef(null);

  // Initialize Uppy when modal opens
  useEffect(() => {
    if (open && !uppy) {
      const restriction = {
        maxFileSize: 4000000000, // 10GB
        maxNumberOfFiles: 1,
        allowedFileTypes: ['.mp3', '.mp4', '.wav', '.ogg', '.aac', '.flac', '.mkv', '.mov', '.m4a']
      };

      const uppyInstance = new Uppy({
        id: 'upload-modal-uppy',
        autoProceed: true,
        debug: false,
        restrictions: restriction,
        locale: getUppyLocal()
      }).use(XHR, {
        endpoint: `${ServerURL}/upload_audiofile/${getLanguage()}`,
        fieldName: 'audiofile',
        getResponseData: async (res) => {
          setIsUploading(false);
          
          if (res.status === 200) {
            matomoEvent('upload file', 'web', 'success');
            transcribe(res.response);
            
            // Call success callback first
            if (onUploadSuccess) {
              onUploadSuccess();
            }
            // Note: Don't call handleClose here as onUploadSuccess should handle modal closing
          } else {
            matomoEvent('upload file', 'web', 'failed');
            setUploadError(res.response || 'Upload failed. Please try again.');
          }
        }
      });

      // Set up event listeners
      uppyInstance.on('upload', () => {
        setIsUploading(true);
        setUploadError(null);
      });

      uppyInstance.on('upload-error', (file, error) => {
        setIsUploading(false);
        setUploadError(error.message || 'Upload failed. Please try again.');
      });

      uppyInstance.on('cancel-all', () => {
        setIsUploading(false);
      });

      setUppy(uppyInstance);
      uppyRef.current = uppyInstance;
    }
  }, [open, uppy, onUploadSuccess]);

  // Cleanup Uppy instance when modal closes
  useEffect(() => {
    if (!open && uppy) {
      // Modal is closing, cleanup Uppy with a small delay to prevent race conditions
      const cleanup = async () => {
        try {
          if (uppy && !uppy.destroyed) {
            uppy.cancelAll();
            await new Promise(resolve => setTimeout(resolve, 100)); // Small delay
            uppy.close({ reason: 'user' });
          }
        } catch (error) {
          console.warn('Error during Uppy cleanup:', error);
        } finally {
          setUppy(null);
          uppyRef.current = null;
          setIsUploading(false);
          setUploadError(null);
          setShowCancelConfirmation(false);
        }
      };
      cleanup();
    }
  }, [open, uppy]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (uppyRef.current) {
        try {
          if (!uppyRef.current.destroyed) {
            uppyRef.current.cancelAll();
            uppyRef.current.close({ reason: 'unmount' });
          }
        } catch (error) {
          console.warn('Error during Uppy unmount cleanup:', error);
        } finally {
          uppyRef.current = null;
        }
      }
    };
  }, []);

  const handleClose = () => {
    // If currently uploading, show confirmation dialog
    if (isUploading) {
      setShowCancelConfirmation(true);
      return;
    }
    
    // Otherwise, close normally
    onClose();
  };

  const handleConfirmCancel = () => {
    // Cancel the upload and close
    if (uppy) {
      uppy.cancelAll();
    }
    setShowCancelConfirmation(false);
    onClose();
  };

  const handleCancelConfirmationClose = () => {
    setShowCancelConfirmation(false);
  };

  // No actions needed - user can use the X button to close

  return (
    <>
      <BaseModal
        open={open}
        onClose={handleClose}
        title={getSentence(123)}
        size="medium"
        maxWidth="sm"
        disableBackdropClick={isUploading}
        disableEscapeKeyDown={isUploading}
      contentSx={{ 
        minHeight: { xs: '300px', sm: '400px' },
        '& .uppy-Dashboard': {
          border: 'none !important',
          position: 'relative !important',
          zIndex: 1,
          borderRadius: '12px !important',
          overflow: 'hidden'
        },
        '& .uppy-Dashboard-inner': {
          border: '2px dashed #e0e0e0 !important',
          borderRadius: '12px !important',
          backgroundColor: 'rgba(0, 0, 0, 0.02) !important',
          transition: 'all 0.3s ease !important',
          minHeight: '280px !important',
          display: 'flex !important',
          alignItems: 'center !important',
          justifyContent: 'center !important',
          position: 'relative !important',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '120px',
            height: '120px',
            backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(`
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
                <path d="M12 18v-6"/>
                <path d="M9 15l3-3 3 3"/>
              </svg>
            `)}")`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            opacity: '0.1',
            color: '#666',
            zIndex: '0'
          }
        },
        '& .uppy-Dashboard-AddFiles': {
          padding: '40px 20px !important',
          textAlign: 'center !important',
          position: 'relative !important',
          zIndex: '1'
        },
        '& .uppy-Dashboard-AddFiles-title': {
          fontSize: '1.2rem !important',
          fontWeight: '600 !important',
          marginBottom: '12px !important',
          color: '#333 !important'
        },
        '& .uppy-Dashboard-dropFilesHereHint': {
          fontSize: '1.1rem !important',
          fontWeight: '500 !important',
          color: '#666 !important',
          marginBottom: '8px !important'
        },
        '& .uppy-Dashboard-browse': {
          color: '#2196f3 !important',
          textDecoration: 'underline !important',
          fontWeight: '600 !important'
        },
        '& .uppy-Dashboard-note': {
          fontSize: '0.9rem !important',
          color: '#666 !important',
          marginTop: '8px !important'
        },
        // Hover effects
        '& .uppy-Dashboard-inner:hover': {
          borderColor: '#2196f3 !important',
          backgroundColor: 'rgba(33, 150, 243, 0.05) !important'
        },
        // Prevent Uppy from interfering with modal events
        '& .uppy-Dashboard-overlay': {
          pointerEvents: 'none !important'
        },
        // Style the file items
        '& .uppy-Dashboard-Item': {
          borderRadius: '8px !important',
          border: '1px solid #e0e0e0 !important'
        },
        // Progress styling
        '& .uppy-ProgressBar': {
          backgroundColor: '#f5f5f5 !important',
          borderRadius: '4px !important',
          overflow: 'hidden !important',
          marginBottom: '16px !important'
        },
        '& .uppy-ProgressBar-fill': {
          backgroundColor: '#2196f3 !important',
          transition: 'width 0.3s ease !important'
        }
      }}
    >
      {/* Warning message */}
      <Alert 
        severity="error" 
        sx={{ 
          mb: 2,
          fontSize: { xs: '0.875rem', sm: '1rem' }
        }}
      >
        {getSentence(74) || 'Please upload audio files only'}
      </Alert>

      {/* Error message */}
      {uploadError && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2,
            fontSize: { xs: '0.875rem', sm: '1rem' }
          }}
          onClose={() => setUploadError(null)}
        >
          <div dangerouslySetInnerHTML={{ __html: uploadError }} />
        </Alert>
      )}

      {/* Upload interface */}
      <Box sx={{ opacity: isUploading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        {uppy && (
          <>
            <ProgressBar
              uppy={uppy}
              hideAfterFinish={false}
            />
            
             <Dashboard
               proudlyDisplayPoweredByUppy={false}
               uppy={uppy}
               plugins={['GoogleDrive']}
               metaFields={[
                 { id: 'name', name: 'Name', placeholder: 'File name' }
               ]}
               height={300}
               showProgressDetails={true}
               hideUploadButton={false}
               hideRetryButton={false}
               hidePauseResumeButton={false}
               hideCancelButton={false}
               closeModalOnClickOutside={false}
               closeAfterFinish={false}
               note="Supported formats: MP3, MP4, WAV, OGG, AAC, FLAC, MKV, MOV, M4A (max 4GB)"
             />
          </>
        )}
        
         {!uppy && (
           <Box 
             sx={{ 
               display: 'flex', 
               flexDirection: 'column',
               alignItems: 'center', 
               justifyContent: 'center', 
               height: 300,
               border: '2px dashed #e0e0e0',
               borderRadius: 3,
               backgroundColor: 'rgba(0, 0, 0, 0.02)',
               position: 'relative',
               '&::before': {
                 content: '""',
                 position: 'absolute',
                 top: '50%',
                 left: '50%',
                 transform: 'translate(-50%, -50%)',
                 width: '120px',
                 height: '120px',
                 backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(`
                   <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                     <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                     <polyline points="14,2 14,8 20,8"/>
                     <path d="M12 18v-6"/>
                     <path d="M9 15l3-3 3 3"/>
                   </svg>
                 `)}")`,
                 backgroundSize: 'contain',
                 backgroundRepeat: 'no-repeat',
                 backgroundPosition: 'center',
                 opacity: '0.1',
                 color: '#666',
                 zIndex: '0'
               }
             }}
           >
             <CloudUploadIcon 
               sx={{ 
                 fontSize: 48, 
                 color: '#2196f3',
                 mb: 2,
                 animation: 'pulse 2s infinite',
                 '@keyframes pulse': {
                   '0%': { opacity: 0.6 },
                   '50%': { opacity: 1 },
                   '100%': { opacity: 0.6 }
                 }
               }} 
             />
             <Typography 
               color="text.secondary" 
               sx={{ 
                 fontSize: '1rem',
                 fontWeight: 500,
                 position: 'relative',
                 zIndex: 1
               }}
             >
               Initializing upload interface...
             </Typography>
           </Box>
         )}
      </Box>

        {/* Recording warning */}
        {isRecording && (
          <Alert 
            severity="info" 
            sx={{ 
              mt: 2,
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            Note: You are currently recording. The page will not reload after upload to preserve your recording session.
          </Alert>
        )}
      </BaseModal>

      {/* Cancel Upload Confirmation Dialog */}
      <ConfirmationDialog
        open={showCancelConfirmation}
        title={getSentence(143)}
        message={getSentence(144)}
        confirmText={getSentence(145)}
        cancelText={getSentence(146)}
        onConfirm={handleConfirmCancel}
        onCancel={handleCancelConfirmationClose}
      />
    </>
  );
};

export default UploadModal;
